import { IProjectShort } from 'types/project';

import { IGetByIdProject } from './models';

import { AApiBase } from '../apiBase/AApiBase';
import type { IBaseResponse } from '../apiBase/IBaseResponse';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';
import { IUserRole } from '../apiUsers/models';

class ApiProjects extends AApiBase {
  protected baseUrl = '/api';
  protected withHeader = true;

  private static __instance: ApiProjects | undefined;

  private static get instance(): ApiProjects {
    if (ApiProjects.__instance === undefined) {
      ApiProjects.__instance = new ApiProjects();
    }
    return ApiProjects.__instance;
  }

  public static getAllProjects = async ({
    limit = 1000,
    offset = 0,
    sort_by,
  }: {
    limit?: number;
    offset?: number;
    sort_by?: string;
  }) => {
    type Return = { items: IProjectShort[]; archived_items: IProjectShort[] };

    try {
      const urlParams = new URLSearchParams();
      urlParams.append('limit', limit.toString());
      urlParams.append('offset', offset.toString());
      if (sort_by) {
        urlParams.append('sort_by', sort_by);
      }

      const response = await ApiProjects.instance.get<IBaseResponse<Return>>(
        '/projects' + '?' + urlParams.toString(),
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<Return>({ error });
    }
  };

  public static getProjectById = async (id: number) => {
    try {
      const response = await ApiProjects.instance.get<IBaseResponse<IGetByIdProject>>(
        `/projects/${id}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<IGetByIdProject>({ error });
    }
  };

  public static deleteProject = async (id: number) => {
    try {
      const response = await ApiProjects.instance.delete<IBaseResponse<any>>(`/projects/${id}`);

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static createProject = async ({
    name,
    users,
    notes,
  }: {
    name: string;
    users: { id: number; role: IUserRole }[];
    notes: string;
  }) => {
    try {
      const response = await ApiProjects.instance.post<IBaseResponse<{ id: number }>>('/projects', {
        name,
        users,
        notes,
      });

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<{ id: number }>({ error });
    }
  };

  public static updateProject = async ({
    id,
    name,
    users,
    notes,
  }: {
    id: number;
    name: string;
    users: { id: number; role: IUserRole }[];
    notes: string;
  }) => {
    try {
      const response = await ApiProjects.instance.patch<IBaseResponse<{ id: number }>>(
        `/projects/${id}`,
        {
          name,
          users,
          notes,
        },
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error: unknown) {
      return mapError<{ id: number }>({ error });
    }
  };

  public static archiveProject = async ({ project_id }: { project_id: number }) => {
    try {
      const response = await ApiProjects.instance.patch<IBaseResponse<any>>(
        `/projects/archive/${project_id}`,
        {},
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };
}

export { ApiProjects };
